import { useCreateNook, useDeleteNook, useUpdateNook, type Nook, type NookListItem } from "@nook/api-client"
import { formatFullDate, inline, joinNote, lineSafe, normalizeNote, readNote, splitNote, writeNote, type Mark, type NoteLine, type Run } from "@nook/client-ui"
import { cn, toast, Toggle } from "@nook/ui"
import { BulletList, ListItem, OrderedList } from "@tiptap/extension-list"
import { Placeholder } from "@tiptap/extensions"
import { MarkdownManager } from "@tiptap/markdown"
import { Slice } from "@tiptap/pm/model"
import { EditorContent, Extension, generateHTML, useEditor, useEditorState, wrappingInputRule, type Editor, type JSONContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Marked } from "marked"
import { Bold, Heading2, Italic, List, ListOrdered, Strikethrough } from "lucide-react"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

/**
 * A note whose whole body is `user_note` (block 47). Notes captured before 47
 * kept their body in `content` and open the old way.
 */
export const isNote = (nook: NookListItem | Nook) => nook.isNote && !nook.excerpt

/** Out of any list the selection is in, as iOS leaves one: a block is a heading, an item or a paragraph, never two. */
const unlist = (editor: Editor) => {
  while (editor.isActive("listItem")) if (!editor.commands.liftListItem("listItem")) break
  return editor.chain().focus()
}

/**
 * A note's formats, the iOS editor's `FORMATS` in the same order. Nothing else
 * parses, so nothing else is written. H1 is the title field's alone.
 */
const FORMATS = [
  { label: "Heading", icon: Heading2, active: (e: Editor) => e.isActive("heading"), run: (e: Editor) => unlist(e).toggleHeading({ level: 2 }).run() },
  { label: "Bold", icon: Bold, active: (e: Editor) => e.isActive("bold"), run: (e: Editor) => e.chain().focus().toggleBold().run() },
  { label: "Italic", icon: Italic, active: (e: Editor) => e.isActive("italic"), run: (e: Editor) => e.chain().focus().toggleItalic().run() },
  { label: "Strikethrough", icon: Strikethrough, active: (e: Editor) => e.isActive("strike"), run: (e: Editor) => e.chain().focus().toggleStrike().run() },
  { label: "Bulleted list", icon: List, active: (e: Editor) => e.isActive("bulletList"), run: (e: Editor) => e.chain().focus().setParagraph().toggleBulletList().run() },
  { label: "Numbered list", icon: ListOrdered, active: (e: Editor) => e.isActive("orderedList"), run: (e: Editor) => e.chain().focus().setParagraph().toggleOrderedList().run() },
]

/** Nesting below the top level, 0 there. iOS stops at 5. */
const listDepth = (editor: Editor) => {
  const { $from } = editor.state.selection
  let items = 0
  for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === "listItem") items++
  return items - 1
}

/**
 * The iOS editor's keys (`react-native-enriched-markdown`): Return continues a
 * list, and on an empty item leaves the list with the line kept empty; Return
 * inside a heading carries on as a paragraph; Backspace at an item's start
 * outdents, then un-lists; Tab indents an item or starts a list, Shift-Tab
 * outdents. Shift-Return is Return: a line is a block on iOS.
 */
export const NoteKeys = Extension.create({
  name: "noteKeys",
  priority: 1000,
  addKeyboardShortcuts() {
    const editor = this.editor
    const enter = () => {
      const { $from } = editor.state.selection
      if (editor.isActive("listItem")) return $from.parent.content.size ? editor.commands.splitListItem("listItem") : unlist(editor).splitBlock().run()
      return editor.isActive("heading") && $from.parentOffset > 0 && editor.chain().splitBlock().setParagraph().run()
    }
    return {
      Enter: enter,
      "Shift-Enter": () => enter() || editor.commands.splitBlock(),
      Backspace: () => {
        const { empty, $from } = editor.state.selection
        return empty && $from.parentOffset === 0 && editor.isActive("listItem") && $from.index(-1) === 0 && editor.commands.liftListItem("listItem")
      },
      Tab: () => (editor.isActive("listItem") ? listDepth(editor) < 5 && editor.commands.sinkListItem("listItem") : !editor.isActive("heading") && editor.commands.toggleBulletList()) || true,
      "Shift-Tab": () => (editor.isActive("listItem") && editor.commands.liftListItem("listItem")) || true,
    }
  },
})

export const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2] },
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    listKeymap: false,
    code: false,
    codeBlock: false,
    hardBreak: false,
    horizontalRule: false,
    link: false,
    underline: false,
  }),
  BulletList,
  // `1. ` and `1) ` start a list, numbered from 1 as iOS numbers one.
  OrderedList.extend({
    addInputRules() {
      return [wrappingInputRule({ find: /^\d+[.)]\s$/, type: this.type })]
    },
  }),
  // An item is one line, as on iOS; nested lists follow it.
  ListItem.extend({ content: "paragraph (bulletList | orderedList)*", addKeyboardShortcuts: () => ({}) }),
]

// Inline markdown only: blocks come from `readNote`. No bare-URL links, as md4c makes none on iOS. Its own
// `Marked`, not the global one; typed as `marked`, but it uses only what an instance has (`lexer`, `Lexer`, `use`, `setOptions`, `defaults`).
const markdown = new MarkdownManager({ marked: new Marked({ tokenizer: { url: () => undefined } }) as never, extensions: EXTENSIONS })
const inlineNodes = (text: string) => markdown.parse(lineSafe(text)).content?.[0]?.content ?? []

/** A note body as the editor's document, nested from `readNote`'s lines as iOS nests it. */
export function noteDoc(body: string): JSONContent {
  const content: JSONContent[] = []
  const lists: JSONContent[] = []
  for (const line of readNote(normalizeNote(body))) {
    const block = { type: line.type === "h" ? "heading" : "paragraph", ...(line.type === "h" && { attrs: { level: 2 } }), content: inlineNodes(line.text) }
    if (line.type === "p" || line.type === "h") {
      lists.length = 0
      content.push(block)
      continue
    }
    const type = line.type === "ul" ? "bulletList" : "orderedList"
    lists.length = line.depth + 1
    if (lists[line.depth]?.type !== type) {
      const list = (lists[line.depth] = { type, content: [] })
      ;(line.depth ? lists[line.depth - 1]!.content!.at(-1)!.content! : content).push(list)
    }
    lists[line.depth]!.content!.push({ type: "listItem", content: [block] })
  }
  return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] }
}

const MARKS: Record<string, Mark> = { bold: "bold", italic: "italic", strike: "strike" }
const runs = (node: JSONContent): Run[] => (node.content ?? []).map((text) => ({ text: text.text ?? "", marks: (text.marks ?? []).flatMap((mark) => MARKS[mark.type] ?? []) }))

/** The editor's document as note lines. A second paragraph in an item is its own line, as iOS reads one. */
export function lines(nodes: JSONContent[] = [], depth = 0): NoteLine[] {
  return nodes.flatMap((node): NoteLine[] =>
    node.type === "bulletList" || node.type === "orderedList"
      ? (node.content ?? []).flatMap((item) =>
          (item.content ?? []).flatMap((child, i) =>
            child.type === "paragraph" && !i ? [{ type: node.type === "bulletList" ? "ul" : "ol", depth, text: inline(runs(child)) }] : lines([child], depth + 1),
          ),
        )
      : [{ type: node.type === "heading" ? "h" : "p", depth: 0, text: inline(runs(node)) }],
  )
}

/** A note's markdown as the editor would show it, for read-only places like the card. */
export const noteHtml = (text: string) => generateHTML(noteDoc(text), EXTENSIONS)

const PLACEHOLDER =
  "[&_.is-editor-empty:first-child]:before:text-muted-foreground [&_.is-editor-empty:first-child]:before:pointer-events-none [&_.is-editor-empty:first-child]:before:float-left [&_.is-editor-empty:first-child]:before:h-0 [&_.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]"

/** Lines flush, as on iOS; lists indented and marked as iOS draws them. Shared with the card. */
export const NOTE_BLOCKS = "[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-[1.1em] [&_ol]:pl-[1.1em] [&_li]:pl-[0.15em] [&_li::marker]:text-muted-foreground"

/**
 * A note body in, the stored form out (`@nook/client-ui`'s `writeNote`), the
 * same bytes the iOS editor writes. Plain text pasted from elsewhere reads as
 * markdown, as iOS pastes it; a copy from a note keeps its formatting.
 * Uncontrolled after mount, so a save landing mid-typing cannot move the caret.
 */
export function useRichText({ value, onChange, placeholder, autoFocus, className }: {
  value: string
  onChange: (markdown: string) => void
  placeholder: string
  autoFocus?: boolean
  className?: string
}) {
  const change = useRef(onChange)
  change.current = onChange

  return useEditor({
    extensions: [...EXTENSIONS, NoteKeys, Placeholder.configure({ placeholder })],
    content: noteDoc(value),
    // iOS turns `- ` and `1. ` into lists as you type, and nothing else.
    enableInputRules: ["bulletList", "orderedList"],
    enablePasteRules: false,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn("outline-none [&_h2]:text-lg [&_h2]:font-semibold [&_p]:min-h-[1lh]", NOTE_BLOCKS, PLACEHOLDER, className),
      },
      handleKeyDown: (view, event) => event.key === "Escape" && (view.dom.blur(), true),
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData("text/plain")
        if (!text || event.clipboardData!.getData("text/html").includes("data-pm-slice")) return false
        view.dispatch(view.state.tr.replaceSelection(Slice.maxOpen(view.state.schema.nodeFromJSON(noteDoc(text)).content)).scrollIntoView())
        return true
      },
    },
    onUpdate: ({ editor }) => change.current(writeNote(lines(editor.getJSON().content))),
  })
}

export function FormatBar({ editor, className }: { editor: Editor | null; className?: string }) {
  const active = useEditorState({ editor, selector: ({ editor }) => (editor ? FORMATS.map((format) => format.active(editor)) : []) })

  return editor ? (
    <div role="toolbar" aria-label="Format" className={cn("bg-popover flex w-fit gap-0.5 rounded-full border p-1 shadow-sm", className)}>
      {FORMATS.map((format, i) => (
        <Toggle
          key={format.label}
          size="sm"
          aria-label={format.label}
          title={format.label}
          className="rounded-full"
          pressed={active?.[i] ?? false}
          // Keeps the selection the format applies to.
          onMouseDown={(event) => event.preventDefault()}
          onPressedChange={() => format.run(editor)}
        >
          <format.icon />
        </Toggle>
      ))}
    </div>
  ) : null
}

/**
 * Autosaves `user_note` (block 37): a field the user has to remember to commit
 * is a field that loses notes, so the status stands in for a Save button.
 * With no nook yet, the first words create one (block 47), and leaving a note
 * emptied deletes it, as Apple Notes does, with an Undo.
 */
export function useNoteAutosave(nook: Pick<Nook, "id" | "userNote"> | null, { onCreated, deleteIfEmpty = false }: { onCreated?: ((id: string) => void) | undefined; deleteIfEmpty?: boolean } = {}) {
  const update = useUpdateNook()
  const create = useCreateNook()
  const remove = useDeleteNook()
  const [value, setValue] = useState(nook?.userNote ?? "")
  const id = useRef(nook?.id ?? null)
  const creating = useRef(false)
  const mounted = useRef(true)

  // What the server last acknowledged. A ref: the unmount save reads it without a render.
  const saved = useRef(value)
  const latest = useRef(value)
  latest.current = value
  const lastText = useRef(value)
  if (value.trim()) lastText.current = value

  const save = useCallback(() => {
    if (value === saved.current || creating.current) return

    const previous = saved.current
    // Claimed before the request so a blur straight after the debounce does not
    // send the same note twice; put back on failure so the next edit retries.
    saved.current = value
    const onError = () => {
      saved.current = previous
    }

    if (id.current) return update.mutate({ id: id.current, userNote: value.trim() || null }, { onError })
    if (!value.trim()) return

    creating.current = true
    // A promise, not `mutate`'s callbacks: those do not run once the editor
    // has unmounted, and text typed during the create still has to follow it.
    create.mutateAsync({ text: value }).then(
      ({ id: created }) => {
        id.current = created
        creating.current = false
        if (mounted.current) onCreated?.(created)
        // Whatever was typed while the note was being created.
        flush.current()
      },
      () => {
        // No retry here, or a rejected create would resend forever; the next edit retries.
        onError()
        creating.current = false
      },
    )
  }, [create, onCreated, update, value])

  // In a ref so the unmount effect can stay `[]`: re-running it per keystroke
  // would make every keystroke a save.
  const flush = useRef(save)
  flush.current = save

  useEffect(() => {
    if (value === saved.current) return

    const timer = setTimeout(() => flush.current(), 800)
    return () => clearTimeout(timer)
  }, [value])

  // Stepping or closing leaves without blurring the field, so this is the last chance to save.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (!deleteIfEmpty || !id.current || latest.current.trim()) return flush.current()

      const text = lastText.current
      remove.mutate(id.current)
      toast("Empty note deleted", { action: { label: "Undo", onClick: () => create.mutate({ text }) } })
    }
  }, [])

  // A failure keeps saying so while the text sits unsent, and an edit made
  // *after* a save clears the status rather than claiming it is stored.
  const status =
    update.isPending || create.isPending
      ? "Saving…"
      : update.isError || create.isError
        ? "Not saved"
        : value !== saved.current
          ? null
          : update.isSuccess || create.isSuccess
            ? "Saved"
            : null

  return { value, setValue, save, status, failed: update.isError || create.isError }
}

/**
 * A note: the date, a title field, then the body. Stored as one `user_note`
 * whose title is a leading `# ` line, so text typed in the body never becomes
 * the title. The same document on `/n/$id` and in the panel.
 */
export function NoteDocument({ nook, onCreated }: { nook: Nook | null; onCreated?: (id: string) => void }) {
  const note = useNoteAutosave(nook, { onCreated, deleteIfEmpty: true })
  const [title, setTitle] = useState(() => splitNote(note.value).title)
  const body = useRef(normalizeNote(splitNote(note.value).body))
  const titleField = useRef<HTMLTextAreaElement>(null)
  const editor = useRichText({
    value: body.current,
    onChange: (markdown) => note.setValue(joinNote(title, (body.current = markdown))),
    placeholder: "Start writing…",
    className: "min-h-[50vh] text-[0.9375rem] leading-relaxed",
  })

  // Grows with the title; `field-sizing` is not in every browser.
  useLayoutEffect(() => {
    const field = titleField.current
    if (!field) return
    field.style.height = "auto"
    field.style.height = `${field.scrollHeight}px`
  }, [title])

  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground text-center text-xs" aria-live="polite">
        {formatFullDate(nook?.editedAt ?? new Date().toISOString())}
        {note.status ? <span className={cn(note.failed && "text-destructive")}> · {note.status}</span> : null}
      </p>
      <textarea
        ref={titleField}
        value={title}
        rows={1}
        placeholder="Title"
        aria-label="Title"
        autoFocus={!nook}
        className="placeholder:text-muted-foreground resize-none overflow-hidden bg-transparent text-[1.75rem] leading-tight font-semibold tracking-tight outline-none"
        onChange={(event) => {
          const next = event.target.value.replace(/\n/g, " ")
          setTitle(next)
          note.setValue(joinNote(next, body.current))
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent.isComposing) return
          event.preventDefault()
          editor?.commands.focus("start")
        }}
        onBlur={note.save}
      />
      <EditorContent editor={editor} onBlur={note.save} />
      <FormatBar editor={editor} className="sticky bottom-6 mx-auto" />
    </div>
  )
}
